// src/store/auth.js
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useToast } from 'vue-toastification'
import authService from '../services/authService'
import { getSidebarConfig } from '../services/permissionService'
import sidebarPermissions from '../../sidebar-permissions.json'

export const useAuthStore = defineStore('auth', () => {
  // State
  const sidebarNavigation = ref(sidebarPermissions.navigation)
  const sidebarRoles = ref(sidebarPermissions.roles)
  const user = ref(JSON.parse(localStorage.getItem('user')) || null)
  const token = ref(localStorage.getItem('token') || null)
  const isLoading = ref(false)
  const companyViewMode = ref(null) // Store company data when viewing as company

  // Getters
  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const userRole = computed(() => {
    // If in company view mode, return Administrator
    if (companyViewMode.value) {
      return 'Administrator'
    }
    return user.value?.role || null
  })
  const userCompanyId = computed(() => {
    // If in company view mode, return the company ID
    if (companyViewMode.value) {
      return companyViewMode.value.id
    }
    return user.value?.companyId || null
  })
  const isCompanyViewMode = computed(() => !!companyViewMode.value)
  const userName = computed(() => {
    if (!user.value) {
      console.log('No user data available')
      return 'User'
    }
    
    const firstName = user.value.firstName || ''
    const lastName = user.value.lastName || ''
    
    console.log('Computing userName with:', { firstName, lastName, email: user.value.email })
    
    if (firstName && lastName) {
      return `${firstName} ${lastName}`
    } else if (firstName) {
      return firstName
    } else if (lastName) {
      return lastName
    } else if (user.value.email) {
      return user.value.email.split('@')[0] // Use email username as fallback
    } else {
      return 'User'
    }
  })

  // Actions
  const login = async (credentials) => {
    try {
      isLoading.value = true
      const response = await authService.login(credentials)
      
      token.value = response.token
      user.value = response.user
      
      localStorage.setItem('token', response.token)
      localStorage.setItem('user', JSON.stringify(response.user))
      
      // Fetch dynamic sidebar permissions
      await fetchSidebarConfig()
      
      const toast = useToast()
      toast.success('Login successful!')
      
      return response
    } catch (error) {
      const toast = useToast()
      
      // Handle role-based access error
      if (error.response?.status === 403 && error.response?.data?.code === 'ROLE_NOT_ALLOWED') {
        toast.error('Access denied. Your role does not have permission to access this system.')
      } else {
        toast.error(error.message || 'Login failed')
      }
      
      throw error
    } finally {
      isLoading.value = false
    }
  }

  const register = async (userData) => {
    try {
      isLoading.value = true
      const response = await authService.register(userData)
      
      const toast = useToast()
      toast.success('Registration successful! Please login.')
      
      return response
    } catch (error) {
      const toast = useToast()
      toast.error(error.message || 'Registration failed')
      throw error
    } finally {
      isLoading.value = false
    }
  }

  const logout = () => {
    token.value = null
    user.value = null
    companyViewMode.value = null
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    localStorage.removeItem('companyViewMode')
    localStorage.removeItem('contractorViewMode')
    
    const toast = useToast()
    toast.success('Logged out successfully')
  }

  const initializeAuth = () => {
    token.value = localStorage.getItem('token')
    const storedUser = localStorage.getItem('user')
    
    console.log('[AuthStore] Initializing auth...')
    console.log('[AuthStore] Token present:', !!token.value)
    console.log('[AuthStore] User present:', !!storedUser)

    if (token.value && storedUser) {
      try {
        user.value = JSON.parse(storedUser)
        console.log('[AuthStore] Auth initialized successfully for:', user.value.email)
        
        // Fetch dynamic sidebar permissions config
        fetchSidebarConfig()
      } catch (e) {
        console.error('[AuthStore] Failed to parse stored user:', e)
        logout()
      }
    } else {
      console.log('No stored auth data found')
    }
    
    // Initialize company view mode
    initializeCompanyViewMode()
  }

  const updateUser = (userData) => {
    user.value = { ...user.value, ...userData }
    localStorage.setItem('user', JSON.stringify(user.value))
  }

  const hasPermission = (permission) => {
    if (!user.value) return false
    
    // Role-based permissions
    const rolePermissions = {
      'PremierAdmin': ['all'],
      'SuperAdmin': ['manage_users', 'manage_companies', 'view_reports', 'manage_settings'],
      'Administrator': ['all'],
      'ParkOwner': ['manage_locations', 'view_reports', 'manage_tariffs'],
      'ParkAdmin': ['manage_transactions', 'view_reports'],
      'ParkOperation': ['manage_transactions'],
      'ParkOperator': ['create_transactions'],
      'Customer': ['view_own_transactions']
    }
    
    const userPermissions = rolePermissions[user.value.role] || []
    return userPermissions.includes('all') || userPermissions.includes(permission)
  }

const normalizeRole = (role) => role ? role.toLowerCase().replace(/[^a-z0-9]/g, '') : ''

  const canAccess = (requiredRoles) => {
    if (!user.value) return false
    if (!requiredRoles || requiredRoles.length === 0) return true
    // In company view mode, use PremierAdmin role
    const currentRole = companyViewMode.value ? 'PremierAdmin' : user.value.role
    const normCurrent = normalizeRole(currentRole)
    return requiredRoles.some(role => normalizeRole(role) === normCurrent)
  }

  const hasGranularPermission = (path, action) => {
    // PremierAdmin gets full access always
    const role = userRole.value
    if (!role) return false
    if (role === 'PremierAdmin') return true

    // Find the navigation item matching the path
    let matchedItem = null
    for (const section of sidebarNavigation.value) {
      if (!section.items) continue
      matchedItem = section.items.find(item => item.path === path)
      if (matchedItem) break
    }

    if (!matchedItem) return false
    
    const normRole = normalizeRole(role)
    const isAllowedRole = matchedItem.roles.some(r => normalizeRole(r) === normRole)
    if (!isAllowedRole) return false

    // If permissions config exists for the role, check actions
    if (matchedItem.permissions) {
      const permissionKey = Object.keys(matchedItem.permissions).find(k => normalizeRole(k) === normRole)
      if (permissionKey && matchedItem.permissions[permissionKey]) {
        const actions = matchedItem.permissions[permissionKey].actions || []
        return actions.includes(action)
      }
    }

    // Default fallback: if role has high-level access but no granular permissions are defined, grant full access
    return true
  }

  const getGranularScope = (path) => {
    const role = userRole.value
    if (!role) return 'own'
    if (role === 'PremierAdmin') return 'all'

    let matchedItem = null
    for (const section of sidebarNavigation.value) {
      if (!section.items) continue
      matchedItem = section.items.find(item => item.path === path)
      if (matchedItem) break
    }

    const normRole = normalizeRole(role)
    if (matchedItem && matchedItem.permissions) {
      const permissionKey = Object.keys(matchedItem.permissions).find(k => normalizeRole(k) === normRole)
      if (permissionKey && matchedItem.permissions[permissionKey] && matchedItem.permissions[permissionKey].scope) {
        return matchedItem.permissions[permissionKey].scope
      }
    }

    // Fallback defaults
    if (normRole === 'superadmin') return 'all'
    if (normRole === 'customer') return 'own'
    if (normRole === 'locationmanager') return 'location'
    return 'company'
  }

  const setCompanyViewMode = (company) => {
    companyViewMode.value = company
    localStorage.setItem('companyViewMode', JSON.stringify(company))
  }

  const clearCompanyViewMode = () => {
    companyViewMode.value = null
    localStorage.removeItem('companyViewMode')
  }

  const initializeCompanyViewMode = () => {
    const stored = localStorage.getItem('companyViewMode')
    if (stored) {
      try {
        companyViewMode.value = JSON.parse(stored)
      } catch (error) {
        console.error('Error parsing stored company view mode:', error)
        localStorage.removeItem('companyViewMode')
      }
    }
  }

  const fetchSidebarConfig = async () => {
    try {
      const config = await getSidebarConfig()
      if (config && config.navigation && config.roles) {
        const localOps = sidebarPermissions.navigation.find(s => s.name === 'Operations')
        const localReports = localOps?.items?.find(i => i.path === '/app/reports')
        const remoteOps = config.navigation.find(s => s.name === 'Operations')
        if (remoteOps && localReports) {
          const existingReports = remoteOps.items.find(i => i.path === '/app/reports')
          if (!existingReports) {
            remoteOps.items.push(JSON.parse(JSON.stringify(localReports)))
          } else {
            if (!existingReports.roles || existingReports.roles.length === 0) {
              existingReports.roles = [...localReports.roles]
            }
            existingReports.roles = existingReports.roles.filter(r => r !== 'PremierAdmin')
            if (existingReports.requiresHasReports === undefined) {
              existingReports.requiresHasReports = true
            }
          }
        }
        sidebarNavigation.value = config.navigation
        sidebarRoles.value = config.roles
        console.log('[AuthStore] Sidebar config loaded dynamically')
      }
    } catch (error) {
      console.warn('[AuthStore] Failed to load dynamic sidebar config, using local fallback:', error)
    }
  }

  return {
    // State
    sidebarNavigation,
    sidebarRoles,
    user,
    token,
    isLoading,
    companyViewMode,
    
    // Getters
    isAuthenticated,
    userRole,
    userCompanyId,
    userName,
    isCompanyViewMode,
    
    // Actions
    login,
    register,
    logout,
    initializeAuth,
    updateUser,
    hasPermission,
    canAccess,
    hasGranularPermission,
    getGranularScope,
    setCompanyViewMode,
    clearCompanyViewMode,
    initializeCompanyViewMode,
    fetchSidebarConfig
  }
})
